import type { FC, ReactNode } from "react";
import { memo } from "react";

import Card from "@/src/common/icons/Card";
import ReturningArrows from "@/src/common/icons/ReturningArrows";
import Star from "@/src/common/icons/Star";
import { languageSelector } from "@/src/contexts";
import * as stylex from "@stylexjs/stylex";

import { CashbackFlame } from "@Assets/CashbackFlame";

import { ComposableSpacing, type OmitStyles, type QueryXsMax, colors } from "../../styles/tokens.stylex";
import type { ISpecialCashback } from "../cashback/CashbackCommonInterfaces";
import Pencil from "../common/icons/Pencil";

export type IInfoLabel = OmitStyles<{
    iconType: InfoLabelIcon;
    labelType: InfoLabelAppearance;
    text?: string;
    svgProps?: React.SVGProps<SVGSVGElement>;
    specialCashback?: ISpecialCashback;
    descriptionStyleX?: stylex.StyleXStyles;
}>;

export type InfoLabelIcon = "LoyaltyCard" | "Cashback" | "Flyer" | "Blog" | "Star";

export type InfoLabelAppearance = "blue" | "magenta" | "green";

export const InfoLabel: FC<IInfoLabel> = memo(
    ({ iconType, labelType, text, svgProps, specialCashback, styleX, descriptionStyleX }) => {
        const resources = languageSelector(
            { cbAriaLabel: "Cashback {1}, poprzednio {2}" },
            { cbAriaLabel: "Cashback {1}, previously {2}" },
        );

        const cbAriaLabel = !specialCashback
            ? null
            : resources.cbAriaLabel
                  .replace("{1}", specialCashback?.maxCashback || "")
                  .replace("{2}", text || "");

        let description = <span {...stylex.props(styles.description, descriptionStyleX)}>{text}</span>;

        let logo: ReactNode;
        switch (iconType) {
            case "LoyaltyCard":
                logo = (
                    <span {...stylex.props(styles.cardLogo)}>
                        <Card />;
                    </span>
                );
                break;
            case "Cashback":
                logo = specialCashback ? (
                    <span {...stylex.props(styles.logo)}>
                        <CashbackFlame />
                    </span>
                ) : (
                    <ReturningArrows width={10} {...stylex.props(styles.logo)} />
                );
                description = specialCashback ? (
                    <div {...stylex.props(styles.cashbackHotDeal)} aria-hidden="true">
                        <span>Cashback</span> <b>{specialCashback.maxCashback}</b>
                        {specialCashback.overrideSegmentMaxCashback && (
                            <>
                                {" "}
                                <span {...stylex.props(styles.oldValue)}>{text}</span>
                            </>
                        )}
                    </div>
                ) : null;
                break;
            case "Blog":
                logo = <Pencil {...svgProps} {...stylex.props(styles.logo)} />;
                break;

            case "Star":
                logo = <Star width={14} {...stylex.props(styles.logo)} />;
                break;
            default:
                logo = null;
                break;
        }

        return (
            <div
                {...stylex.props(styles.infoLabelComponent, styles[labelType], styleX)}
                aria-label={cbAriaLabel}
            >
                {logo}
                {text ? description : null}
            </div>
        );
    },
    (prev, next) => {
        return (
            prev.iconType === next.iconType &&
            prev.labelType === next.labelType &&
            prev.text === next.text &&
            prev.specialCashback?.maxCashback === next.specialCashback?.maxCashback
        );
    },
);

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    infoLabelComponent: {
        borderRadius: "1.86em",

        gap: ComposableSpacing.s2,
        paddingBlock: "5px",
        paddingInline: "10px",
        alignItems: "center",
        color: colors.fg_AltDefault,
        display: "inline-flex",

        lineHeight: 1,
    },

    blue: {
        backgroundColor: colors.b_BrandNavy,
        fontSize: "0.6875rem",
    },

    magenta: {
        backgroundColor: colors.b_BrandMagenta,
        fontSize: "0.6875rem",
    },

    green: {
        backgroundColor: colors.b_Success,
        fontSize: "0.75rem",
    },

    description: {
        fontSize: "0.75rem",
    },

    cashbackHotDeal: {
        fontSize: { default: ".875rem", [xsMax]: "0.75rem" },
    },

    oldValue: {
        textDecoration: "line-through",
    },
    logo: {
        height: "0.875rem",
        width: "0.875rem",
    },
    cardLogo: {
        color: colors.fg_AltDefault,
        height: "1rem",
        width: "1rem",
    },
});
